if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');

        this.selectors = {
          variantIdSelect: '[name="id"]',
        };
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get quantity() {
        return this.querySelector('quantity-input');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get select() {
        return this.querySelector('select');
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.bulkOrderFormActions = document.querySelector('.bulk-order-form');
        this.mainProductInfo = document.querySelector('product-info');
        this.container = this.closest('.sticky-atc-bar');

        this.variantData = this.getVariantData();

        // If the PDP does not actually have the pack product block rendered,
        // hide the popup trigger and show a regular select instead.
        this.checkPackBlockOnPDP();

        this.init();

        // Sync quantity from main PDP quantity selector
        this.syncQuantityFromMainPDP();

        // Hidden select change handler (for syncing with main PDP)
        if (this.select) {
          this.select.addEventListener('change', () => {
            // Re-entrancy guard: variant-selects.js updateSelectedSwatchValue syncs back
            // to the sticky bar's hidden select (via the `variantid` attr on PDP radios),
            // which would re-trigger this handler and reset the color. Prevent that.
            if (this._hiddenSelectProcessing) return;
            this._hiddenSelectProcessing = true;

            this.mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
            const selectedVariantId = this.querySelector(this.selectors.variantIdSelect).value;
            this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));

            if (this.mainVariantSelects) {
              // Save scroll position before syncing with main form to prevent auto-scroll
              const savedScrollX = window.scrollX;
              const savedScrollY = window.scrollY;

              // Set global flag so other components (media-gallery, product-info)
              // know to skip any scroll behaviour during this sync cycle.
              window.__stickyAtcSyncing = true;

              // Override scroll methods AND scrollIntoView to block ALL scroll during variant sync
              const originalScrollTo = window.scrollTo.bind(window);
              const originalScroll = window.scroll.bind(window);
              const originalScrollIntoView = Element.prototype.scrollIntoView;
              window.scrollTo = function() {};
              window.scroll = function() {};
              Element.prototype.scrollIntoView = function() {};

              // Cleanup function — restores scroll methods, position, and clears the flag.
              // Called once by whichever fires first: the variantChange event or the safety timeout.
              let cleaned = false;
              const cleanup = () => {
                if (cleaned) return;
                cleaned = true;
                window.scrollTo = originalScrollTo;
                window.scroll = originalScroll;
                Element.prototype.scrollIntoView = originalScrollIntoView;
                // Restore position only if the browser shifted it
                if (window.scrollX !== savedScrollX || window.scrollY !== savedScrollY) {
                  window.scrollTo(savedScrollX, savedScrollY);
                }
                window.__stickyAtcSyncing = false;
              };

              // Store cleanup so syncWithMainProductForm can call it
              // when the variantChange event arrives (async-fetch-complete signal).
              this._pendingSyncCleanup = cleanup;

              // Safety timeout in case the fetch fails / is aborted / never fires variantChange
              this._syncSafetyTimeout = setTimeout(cleanup, 5000);

              Array.from(this.mainVariantSelects.querySelectorAll('select, fieldset'), (element, index) => {
                const variantOptionVal = this.currentVariant.options[index];
                switch (element.tagName) {
                  case 'SELECT':
                    element.value = variantOptionVal;
                    break;
                  case 'FIELDSET':
                    Array.from(element.querySelectorAll('input')).forEach((radio) => {
                      if (radio.value === variantOptionVal) {
                        radio.checked = true;
                        // Use focus with preventScroll to avoid native browser scroll-on-focus
                        radio.focus({ preventScroll: true });
                        radio.dispatchEvent(new Event('change', { bubbles: true }));
                      }
                    });
                    break;
                }
              });
            }

            this.updatePrice();
            this.updateButton(true, '', false);
            if (!this.currentVariant) {
              this.updateButton(true, '', true);
            } else {
              this.updateButton(!this.currentVariant.available, FoxTheme.variantStrings.soldOut);
            }

            // Clear re-entrancy guard after the full async cycle
            setTimeout(() => { this._hiddenSelectProcessing = false; }, 2500);
          });
        }

        // Setup per-option selects (for non-pack products with separate dropdowns)
        this.setupOptionSelects();

        // Setup popup close-on-ATC
        this.setupPopupAtcListener();

        // Clone PDP color swatch fieldsets into popup so they look identical
        this.clonePDPColorSwatches();

        // Wire up popup variant pills to sync with main PDP
        this.setupPopupVariantSync();
      }

      /**
       * Set up individual option selects (for non-pack products).
       * When any option changes, find the matching variant from all selected options
       * and update the hidden variant ID select, which triggers the main sync flow.
       */
      setupOptionSelects() {
        const optionSelects = this.querySelectorAll('.sticky-atc-bar__option-dropdown');
        if (!optionSelects.length) return;

        const hiddenVariantSelect = this.querySelector('select[name="id"]');
        if (!hiddenVariantSelect) return;

        optionSelects.forEach((optSelect) => {
          optSelect.addEventListener('change', () => {
            // Gather selected values from all option selects
            const selectedOptions = Array.from(optionSelects).map(s => s.value);

            // Find the variant that matches all selected options
            const matchedVariant = this.variantData.find((variant) => {
              return variant.options.every((opt, idx) => opt === selectedOptions[idx]);
            });

            if (matchedVariant) {
              // Guard flag: prevent syncOptionSelects from overwriting
              // the dropdown values while we're driving the change from here
              this._stickyOptionChanging = true;
              hiddenVariantSelect.value = matchedVariant.id;
              hiddenVariantSelect.dispatchEvent(new Event('change'));
              // Clear the flag after the PDP async variant-change cycle completes
              setTimeout(() => { this._stickyOptionChanging = false; }, 2500);
            }
          });
        });
      }

      /**
       * If the PDP hasn't rendered a pack product block, the popup shouldn't show.
       * Hide the popup trigger and show a regular visible <select> instead.
       */
      checkPackBlockOnPDP() {
        const popupTrigger = this.querySelector('.sticky-atc-popup__trigger');
        if (!popupTrigger) return; // No popup trigger means it's already a regular select

        // Check if the main PDP actually has the pack product block rendered
        const pdpPackBlock = document.querySelector(
          'product-info .product__block-pack-product'
        );

        if (!pdpPackBlock) {
          // PDP does not have pack block — switch to regular select mode
          popupTrigger.style.display = 'none';

          // Build per-option selects dynamically from the main PDP's variant-selects
          const mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
          if (mainVariantSelects && this.variantData && this.variantData.length) {
            // Collect option names and their unique values
            const mainElements = mainVariantSelects.querySelectorAll('select, fieldset');
            const optionNames = [];
            const optionValues = [];

            mainElements.forEach((el, idx) => {
              let name = '';
              let values = [];
              if (el.tagName === 'FIELDSET') {
                const legend = el.querySelector('.form__label .font-body-bold');
                name = legend ? legend.textContent.replace(':', '').trim() : 'Option ' + (idx + 1);
                values = Array.from(el.querySelectorAll('input[type="radio"]')).map(r => r.value);
              } else if (el.tagName === 'SELECT') {
                const labelFor = el.id;
                name = 'Option ' + (idx + 1);
                values = Array.from(el.options).map(o => o.value);
              }
              optionNames.push(name);
              optionValues.push(values);
            });

            // Create the per-option selects container
            const optionsContainer = document.createElement('div');
            optionsContainer.className = 'sticky-atc-bar__option-selects flex items-center gap-3';

            // Determine currently selected variant options
            const currentVariant = this.variantData.find(v => v.id === this.currentVariant?.id) || this.variantData[0];

            optionNames.forEach((name, idx) => {
              const wrapper = document.createElement('div');
              wrapper.className = 'select sticky-atc-bar__option-select';

              const select = document.createElement('select');
              select.className = 'sticky-atc-bar__option-dropdown form-control form-control--select';
              select.dataset.optionIndex = idx;

              optionValues[idx].forEach((val) => {
                const opt = document.createElement('option');
                opt.value = val;
                opt.textContent = val;
                if (currentVariant && currentVariant.options[idx] === val) {
                  opt.selected = true;
                }
                select.appendChild(opt);
              });

              wrapper.appendChild(select);
              optionsContainer.appendChild(wrapper);
            });

            // Insert the options container before the form buttons
            const formButtons = this.querySelector('.product-form__buttons');
            if (formButtons) {
              formButtons.parentNode.insertBefore(optionsContainer, formButtons);
            } else {
              popupTrigger.parentNode.insertBefore(optionsContainer, popupTrigger);
            }
          }

          // Also hide the popup modal
          const popupId = 'StickyAtcPopup-' + this.dataset.section;
          const popup = document.getElementById(popupId);
          if (popup) popup.style.display = 'none';

          // Show the regular ATC button container (hidden via inline style in Liquid when has_pack_data)
          const formButtons = this.querySelector('.product-form__buttons');
          if (formButtons) formButtons.style.display = '';
        }
      }

      /**
       * Clone the color-swatch fieldsets from the PDP's <variant-selects>
       * into the popup, replacing the Liquid-rendered ones.
       * This guarantees the popup swatches look identical to the PDP
       * (correct images, swatch-type attributes, sizes, etc.).
       */
      clonePDPColorSwatches() {
        const popupId = 'StickyAtcPopup-' + this.dataset.section;
        const popup = document.getElementById(popupId);
        if (!popup) return;

        const popupOptions = popup.querySelector('.sticky-atc-popup__options');
        if (!popupOptions) return;

        const mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
        if (!mainVariantSelects) return;

        const popupFieldsets = Array.from(popupOptions.querySelectorAll('fieldset'));
        const pdpFieldsets = Array.from(mainVariantSelects.querySelectorAll('fieldset'));

        popupFieldsets.forEach((popupFs, idx) => {
          const pdpFs = pdpFieldsets[idx];
          if (!pdpFs) return;

          // Only clone if the PDP fieldset is a color/swatch type
          const pdpSwatchType = pdpFs.getAttribute('data-swatch-type');
          if (!pdpSwatchType) return; // Not a swatch fieldset — keep the popup's button rendering

          // Deep-clone the PDP fieldset
          const cloned = pdpFs.cloneNode(true);

          // Remove the form attribute from cloned radio inputs
          // so they don't interfere with the main PDP form
          cloned.querySelectorAll('input[type="radio"]').forEach((radio) => {
            radio.removeAttribute('form');
            // Give unique IDs to avoid duplicate IDs in the DOM
            if (radio.id) {
              radio.id = 'popup-' + radio.id;
            }
          });

          // Update label "for" attributes to match the new IDs
          cloned.querySelectorAll('label[for]').forEach((label) => {
            if (label.getAttribute('for')) {
              label.setAttribute('for', 'popup-' + label.getAttribute('for'));
            }
          });

          // Remove any size-guide elements from the clone (not needed in popup)
          const sizeGuide = cloned.querySelector('.size_guide_fake-div');
          if (sizeGuide) sizeGuide.remove();

          // Replace the popup's fieldset with the cloned one
          popupFs.parentNode.replaceChild(cloned, popupFs);
        });
      }

      /**
       * Wire up variant pill radios inside the popup so that clicking them
       * syncs the selection to the main PDP's <variant-selects>.
       * This triggers the standard variant-change flow (section re-render,
       * price update, media update, etc.).
       */
      setupPopupVariantSync() {
        const popupId = 'StickyAtcPopup-' + this.dataset.section;
        const popup = document.getElementById(popupId);
        if (!popup) return;

        const popupOptions = popup.querySelector('.sticky-atc-popup__options');
        if (!popupOptions) return;

        const mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
        if (!mainVariantSelects) return;

        popupOptions.addEventListener('change', (event) => {
          const radio = event.target;
          if (radio.tagName !== 'INPUT' || radio.type !== 'radio') return;

          // Update the "Selected: ..." label inside the popup
          const selectedLabel = radio.closest('.product-form__input')?.querySelector('[data-selected-swatch-value]');
          if (selectedLabel) selectedLabel.textContent = radio.value;

          // Save scroll position to prevent auto-scroll
          const savedScrollX = window.scrollX;
          const savedScrollY = window.scrollY;

          // Set global flag so other components skip scroll behaviour
          window.__stickyAtcSyncing = true;

          const originalScrollTo = window.scrollTo.bind(window);
          const originalScroll = window.scroll.bind(window);
          const originalScrollIntoView = Element.prototype.scrollIntoView;
          window.scrollTo = function() {};
          window.scroll = function() {};
          Element.prototype.scrollIntoView = function() {};

          // Cleanup function — same pattern as the hidden select handler
          let cleaned = false;
          const stickyBar = this.closest('sticky-atc-bar') || document.querySelector('sticky-atc-bar');
          const cleanup = () => {
            if (cleaned) return;
            cleaned = true;
            window.scrollTo = originalScrollTo;
            window.scroll = originalScroll;
            Element.prototype.scrollIntoView = originalScrollIntoView;
            if (window.scrollX !== savedScrollX || window.scrollY !== savedScrollY) {
              window.scrollTo(savedScrollX, savedScrollY);
            }
            window.__stickyAtcSyncing = false;
          };

          if (stickyBar) {
            stickyBar._pendingSyncCleanup = cleanup;
            stickyBar._syncSafetyTimeout = setTimeout(cleanup, 5000);
          } else {
            setTimeout(cleanup, 5000);
          }

          // Find which option index this radio belongs to (0-based)
          const fieldsets = Array.from(popupOptions.querySelectorAll('fieldset'));
          const fieldsetIndex = fieldsets.indexOf(radio.closest('fieldset'));

          // Sync each popup fieldset's checked value to the main PDP's variant-selects
          fieldsets.forEach((fieldset, idx) => {
            const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
            if (!checkedRadio) return;

            const mainElements = mainVariantSelects.querySelectorAll('select, fieldset');
            const mainElement = mainElements[idx];
            if (!mainElement) return;

            if (mainElement.tagName === 'FIELDSET') {
              const mainRadio = Array.from(mainElement.querySelectorAll('input[type="radio"]')).find(
                r => r.value === checkedRadio.value
              );
              if (mainRadio && !mainRadio.checked) {
                mainRadio.checked = true;
                mainRadio.focus({ preventScroll: true });
                mainRadio.dispatchEvent(new Event('change', { bubbles: true }));
              }
            } else if (mainElement.tagName === 'SELECT') {
              if (mainElement.value !== checkedRadio.value) {
                mainElement.value = checkedRadio.value;
                mainElement.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          });
        });
      }

      /**
       * Listen for successful add-to-cart inside the popup's pack product,
       * then close the popup automatically.
       */
      setupPopupAtcListener() {
        const popupId = 'StickyAtcPopup-' + this.dataset.section;
        const popup = document.getElementById(popupId);
        if (!popup) return;

        // Watch for cart:build events (fired after successful ATC in bulk-order-js.js)
        document.addEventListener('cart:build', () => {
          if (popup.hasAttribute('open')) {
            // Small delay to let the cart drawer open first
            setTimeout(() => {
              popup.hide && popup.hide();
            }, 300);
          }
        });
      }

      /**
       * Keep the sticky bar's hidden quantity input in sync with the main PDP quantity selector.
       * Whenever the user changes quantity on the main PDP, that value is mirrored here.
       */
      syncQuantityFromMainPDP() {
        const stickyQtyInput = this.querySelector('.sticky-atc-bar__quantity-hidden');
        if (!stickyQtyInput) return;

        // Find the main PDP quantity input
        const mainProductInfo = this.mainProductInfo;
        if (!mainProductInfo) return;

        const mainQtyInput = mainProductInfo.querySelector('quantity-input input[name="quantity"], input[name="quantity"]');
        if (!mainQtyInput) return;

        // Set initial value from main PDP
        stickyQtyInput.value = mainQtyInput.value;

        // Listen for changes on the main PDP quantity input
        const observer = new MutationObserver(() => {
          stickyQtyInput.value = mainQtyInput.value;
        });
        observer.observe(mainQtyInput, { attributes: true, attributeFilter: ['value'] });

        // Also listen for input/change events (covers manual typing and +/- buttons)
        mainQtyInput.addEventListener('change', () => {
          stickyQtyInput.value = mainQtyInput.value;
        });
        mainQtyInput.addEventListener('input', () => {
          stickyQtyInput.value = mainQtyInput.value;
        });
      }

      getVariantData() {
        this.variantData =
          this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      init() {
        // If main product form does not exist, always show sticky bar
        if (!this.productFormActions && !this.bulkOrderFormActions) {
          this.container.classList.add('sticky-atc-bar--show');
          return;
        }

        // Handle device changes (existing behavior)
        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.checkDevice.bind(this);
        this.checkDevice();

        const rootMargin = '10px';

        // Track visibility of both forms
        this.visibilityState = {
          productForm: false,
          bulkOrderForm: false,
        };

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.target === this.productFormActions) {
                this.visibilityState.productForm = entry.isIntersecting;
              }

              if (entry.target === this.bulkOrderFormActions) {
                this.visibilityState.bulkOrderForm = entry.isIntersecting;
              }
            });

            // Show sticky bar ONLY if both are fully out of view
            const shouldShowSticky =
              !this.visibilityState.productForm &&
              !this.visibilityState.bulkOrderForm;

            this.container.classList.toggle(
              'sticky-atc-bar--show',
              shouldShowSticky
            );
          },
          {
            threshold: 0, // ANY part visible counts
            rootMargin,
          }
        );

        // Observe both targets if they exist
        if (this.productFormActions) {
          this.observer.observe(this.productFormActions);
        }

        if (this.bulkOrderFormActions) {
          this.observer.observe(this.bulkOrderFormActions);
        }

        this.syncWithMainProductForm();
      }

      setObserveTarget() {
        if (this.productFormActions) {
          this.observer.observe(this.productFormActions);
        }

        if (this.bulkOrderFormActions) {
          this.observer.observe(this.bulkOrderFormActions);
        }
      }

      checkDevice(e) {
        document.documentElement.style.setProperty('--sticky-atc-bar-height', this.clientHeight + 'px');
      }

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('#product-form-sticky-atc-bar');
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }
      }

      updatePrice() {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );

        if (!priceWrapper || !this.currentVariant) return;

        const unitPrice = unitPriceWrapper && unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = this.currentVariant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!this.currentVariant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        setTimeout(() => {
          let discountedValue = document.querySelector('.product__block--price .f-badge--sale')?.innerHTML;
          let stickyBarDiscountedValue = document.querySelector('.sticky-atc-bar__product-info .f-badge--sale');

          if (discountedValue && stickyBarDiscountedValue) {
            stickyBarDiscountedValue.innerHTML = discountedValue;
          }
        }, 1000);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat))
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            this.currentVariant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${FoxTheme.Currency.getBaseUnit(this.currentVariant)}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else if (unitPriceWrapper) {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          if (!this.mainProductInfo) return;
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          const variantInput = this.querySelector('[name="id"]');

          this.currentVariant = variant;
          if (variantInput) variantInput.value = variant.id;
          this.updatePrice();
          // Update popup price immediately with variant data (fallback),
          // then again after a delay to pick up PDP's server-rendered price
          this.updatePopupPrice();
          setTimeout(() => this.updatePopupPrice(), 1200);

          // Also sync the popup's variant pills to match the PDP's selection
          this.syncPopupVariantPills(variant);

          // Sync per-option selects (non-pack products) with PDP variant
          this.syncOptionSelects(variant);

          this.updateButton(true, '', false);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          }

          // The variantChange event fires at the end of product-info's async fetch cycle.
          // Run pending scroll cleanup now (small delay so the .focus() in the next
          // promise .then() also completes before we restore scroll methods).
          if (this._pendingSyncCleanup) {
            clearTimeout(this._syncSafetyTimeout);
            const pendingCleanup = this._pendingSyncCleanup;
            this._pendingSyncCleanup = null;
            setTimeout(pendingCleanup, 150);
          }
        });
      }

      /**
       * Sync the popup's variant radio pills to match the given variant's options.
       */
      syncPopupVariantPills(variant) {
        const popupId = 'StickyAtcPopup-' + this.dataset.section;
        const popup = document.getElementById(popupId);
        if (!popup || !variant) return;

        const popupFieldsets = popup.querySelectorAll('.sticky-atc-popup__options fieldset');
        popupFieldsets.forEach((fieldset, idx) => {
          const optionValue = variant.options[idx];
          if (!optionValue) return;

          const radio = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
            r => r.value === optionValue
          );
          if (radio && !radio.checked) {
            radio.checked = true;
            // Update the selected label text
            const label = fieldset.querySelector('[data-selected-swatch-value]');
            if (label) label.textContent = optionValue;
          }
        });
      }

      /**
       * Sync the per-option selects in the sticky bar to match the given variant's options.
       * Called when the PDP variant changes, to keep the sticky bar dropdowns in sync.
       */
      syncOptionSelects(variant) {
        // Skip if the change was initiated from the sticky bar's own option selects
        if (this._stickyOptionChanging) return;
        if (!variant) return;
        const optionSelects = this.querySelectorAll('.sticky-atc-bar__option-dropdown');
        if (!optionSelects.length) return;

        optionSelects.forEach((optSelect, idx) => {
          if (variant.options[idx] && optSelect.value !== variant.options[idx]) {
            optSelect.value = variant.options[idx];
          }
        });
      }

      /**
       * Update the popup's price display to match the currently selected variant.
       * Copies the updated price HTML from the main PDP into the popup.
       */
      updatePopupPrice() {
        const popupId = 'StickyAtcPopup-' + this.dataset.section;
        const popup = document.getElementById(popupId);
        if (!popup) return;

        const popupPriceContainer = popup.querySelector('.sticky-atc-popup__price');
        if (!popupPriceContainer) return;

        // Get the updated price from the main PDP
        const pdpPriceContainer = this.mainProductInfo && this.mainProductInfo.querySelector('.product__block--price .f-price');
        if (pdpPriceContainer) {
          // Clone the PDP's price HTML into the popup
          const popupPriceWrapper = popupPriceContainer.querySelector('.f-price');
          if (popupPriceWrapper) {
            popupPriceWrapper.innerHTML = pdpPriceContainer.innerHTML;
            // Copy class list for sale/soldout states
            popupPriceWrapper.className = pdpPriceContainer.className;
          } else {
            // If there's no .f-price wrapper, replace the whole container
            popupPriceContainer.innerHTML = pdpPriceContainer.outerHTML;
          }
        } else {
          // Fallback: update price manually from variant data
          if (!this.currentVariant) return;
          const moneyFormat = FoxTheme.settings.moneyFormat;
          const salePrice = popupPriceContainer.querySelector('.f-price-item--sale');
          const regularPrice = popupPriceContainer.querySelectorAll('.f-price-item--regular');
          const priceWrapper = popupPriceContainer.querySelector('.f-price');

          if (salePrice) {
            salePrice.innerHTML = FoxTheme.Currency.formatMoney(this.currentVariant.price, moneyFormat);
          }

          if (regularPrice.length) {
            if (this.currentVariant.compare_at_price && this.currentVariant.compare_at_price > this.currentVariant.price) {
              regularPrice.forEach(el => {
                el.innerHTML = '<s>' + FoxTheme.Currency.formatMoney(this.currentVariant.compare_at_price, moneyFormat) + '</s>';
              });
              if (priceWrapper) priceWrapper.classList.add('f-price--on-sale');
            } else {
              regularPrice.forEach(el => {
                el.innerHTML = FoxTheme.Currency.formatMoney(this.currentVariant.price, moneyFormat);
              });
              if (priceWrapper) priceWrapper.classList.remove('f-price--on-sale');
            }
          }

          if (priceWrapper) {
            if (!this.currentVariant.available) {
              priceWrapper.classList.add('f-price--sold-out');
            } else {
              priceWrapper.classList.remove('f-price--sold-out');
            }
          }
        }
      }
    }
  );
}
